/**
 * Item page model — V98-3-5 (research §2 items 4, 5, 10, 13, 16–19; ADR-0046
 * Appendix A T6–T15). Pure: no React, no fetch.
 *
 * SoD machine states are shown as states, and the alternatives are offered
 * in rule-18 order:
 *   1. another eligible approver (incl. escalation)
 *   2. ask the approver to delegate (a delivered ask; the clock starts at delivery)
 *   3. hold, if the approver is back before the hard date
 *   4. a compensating review — only when the server unlocks it, never primary
 */
import type { AccessReviewRowStatus, Alternatives } from '@aigovops/core';
import { firstName, fmtDay, nameOf, type Names } from './today-model';

export type StepStatus = 'available' | 'todo' | 'waiting' | 'tried' | 'fails' | 'na' | 'locked' | 'unlocked';
export interface AltStep { id: 'other_approver' | 'ask_delegate' | 'hold' | 'compensating'; title: string; status: StepStatus; detail: string }

export function alternativeSteps(a: Alternatives, names: Names): AltStep[] {
  const o = a.options;
  const who = (id: string | null) => nameOf(names, id, 'the approver');
  const hard = a.hard_date ? fmtDay(a.hard_date) : null;
  const other: AltStep = o.other_approver.available
    ? { id: 'other_approver', title: 'Another eligible approver', status: 'available', detail: `Routed to ${who(o.other_approver.user_id)}` }
    : { id: 'other_approver', title: 'Another eligible approver', status: 'tried', detail: a.route.reasons.length ? `None free · ${(a.route.reason_text ?? a.route.reasons).join(' · ')}` : 'Nobody else is eligible' }; // R3-S8: sentences, not codes
  let ask: AltStep;
  if (!o.ask_delegate.applicable) ask = { id: 'ask_delegate', title: 'Ask the approver to delegate', status: 'na', detail: 'No approver is away who could delegate' };
  else {
    // S5: a non-delegable signature is never delegated — the ask is "sign remotely".
    const verb = o.ask_delegate.ask_kind === 'sign_remote' ? 'to sign remotely' : 'to delegate';
    const title = `Ask ${firstName(who(o.ask_delegate.to_user_id))} ${verb}`;
    if (!o.ask_delegate.ask) ask = { id: 'ask_delegate', title, status: 'todo', detail: 'The ask reaches them even while they are away' };
    else if (o.ask_delegate.satisfied) ask = { id: 'ask_delegate', title, status: 'tried', detail: o.ask_delegate.note };
    else ask = { id: 'ask_delegate', title, status: 'waiting', detail: o.ask_delegate.note };
  }
  let hold: AltStep;
  if (!o.hold.applicable) hold = { id: 'hold', title: 'Hold for the approver', status: 'na', detail: 'Nobody eligible is coming back' };
  else if (o.hold.meets_hard_date) hold = { id: 'hold', title: `Hold for ${firstName(who(o.hold.approver_user_id))}`, status: 'todo', detail: `Back ${fmtDay(o.hold.back_on)}${hard ? ` · before the hard date ${hard}` : ''}` };
  else hold = { id: 'hold', title: `Hold for ${firstName(who(o.hold.approver_user_id))}`, status: 'fails', detail: `Back ${fmtDay(o.hold.back_on)} · after the hard date ${hard ?? ''}`.trim() };
  const comp: AltStep = a.compensating_allowed
    ? { id: 'compensating', title: 'Compensating review', status: 'unlocked', detail: 'Approve yourself with facts, the alternatives tried and an independent check · listed as a known SoD limitation' }
    : { id: 'compensating', title: 'Compensating review', status: 'locked', detail: 'Unlocks only when every option above fails the hard date' };
  return [other, ask, hold, comp];
}

/** The alternatives already tried, phrased for the compensating-review record (editable by the preparer). */
export function alternativesTried(a: Alternatives, names: Names): string[] {
  return alternativeSteps(a, names)
    .filter((s) => s.id !== 'compensating' && (s.status === 'tried' || s.status === 'fails'))
    .map((s) => `${s.title}: ${s.detail}`);
}

/** Rule 10: deadline pressure alone is not a reason. Mirrors the server's floor so the error is stated up front. */
export const FACTS_MIN = 40;
export const NOTE_MIN = 10;
export const CHECK_MAX_DAYS = 30;

export const CHECKER_WHY: Record<string, string> = {
  self_approver: 'you approved it',
  reports_to_approver: 'reports to you',
  audit_firm_member: 'at your audit firm',
  absent: 'away',
};
/** The same reasons, seen by someone other than the self-approver (the escalation owner reassigning a check — U1). */
export const CHECKER_WHY_OTHER: Record<string, string> = {
  self_approver: 'self-approved it',
  reports_to_approver: 'reports to the self-approver',
  audit_firm_member: 'at your audit firm',
  absent: 'away',
  current_checker: 'already the checker',
  you: 'that’s you',
};
export function checkerWhy(why: string | null, viewpoint: 'preparer' | 'other' = 'preparer'): string {
  if (!why) return '';
  if (why.startsWith('in_pair:')) return 'in a conflicting pair on this item';
  return (viewpoint === 'other' ? CHECKER_WHY_OTHER : CHECKER_WHY)[why] ?? why.replace(/_/g, ' ');
}

/**
 * What a checker can say they reviewed — plain nouns (U11). Kinds that only
 * restate the approval are not substantive (rule 19) and are never offered.
 * The ids are the server's record kinds.
 */
export const ARTIFACT_KINDS: Array<{ id: string; label: string }> = [
  { id: 'frozen_package', label: 'The item itself (locked version)' },
  { id: 'evidence', label: 'Evidence' },
  { id: 'policy_version', label: 'Policy' },
  { id: 'ticket', label: 'Ticket or change' },
  { id: 'risk_analysis', label: 'Risk analysis' },
];

/** The first reviewed item, prefilled from what the check is about (S12 `about`) — never parsed from a title. */
export function prefillArtifact(about: { kind: string; id: string; version: number | null } | null | undefined): { kind: string; id: string; version: string } {
  if (!about) return { kind: 'evidence', id: '', version: '' };
  return { kind: about.kind === 'signature_request' ? 'frozen_package' : 'evidence', id: about.id, version: about.version != null ? `v${about.version}` : '' };
}

/** @deprecated Titles are human now (S12); use the row's `about`. Kept for old rows: "… · signature_request sig-1". */
export function checkedItem(title: string): { kind: string; id: string } | null {
  const m = title.match(/·\s*([a-z_]+)\s+(\S+)\s*$/);
  return m ? { kind: m[1]!, id: m[2]! } : null;
}

// ---- access review rows -------------------------------------------------------------------

export type RowAction = 'review' | 'approve' | 'add_party' | null;
export interface RowView {
  row: AccessReviewRowStatus;
  /** What THIS viewer can do on the row right now. */
  action: RowAction;
  /** Full sentence (kept for screen-reader context and tests). */
  state: string;
  /** U13: the ONE chip — a short state ("To review", "Kept", "To remove", "Approved", "Needs an independent party"); "Keep" / "Revoke" are only button labels (T5-H7). */
  label: string;
  /** U13: who did / does what, as plain meta text next to the chip. */
  meta: string;
  /** Exceptions: reciprocal, nobody eligible, revoke decisions, the viewer's own access. */
  exception: boolean;
}

export function rowView(r: AccessReviewRowStatus, me: string, names: Names): RowView {
  const subjectIsMe = r.subject_user_id === me;
  let action: RowAction = null;
  let label: string;
  let meta: string;
  const you = (id: string | null) => (id === me ? 'you' : nameOf(names, id));
  if (r.reviewer.status === 'needs_independent_party') {
    action = 'add_party';
    label = 'Needs an independent party';
    meta = 'nobody else can review it';
  } else if (!r.decision) {
    if (r.reviewer.user_id === me && !subjectIsMe) action = 'review';
    label = r.reviewer.user_id ? 'To review' : 'Nobody eligible';
    meta = r.reviewer.user_id ? `reviewer: ${you(r.reviewer.user_id)}` : 'nobody eligible to review';
  } else if (!r.approval) {
    const reviewedByMe = r.decision.decided_by_user_id === me;
    if (r.approver.user_id === me && !reviewedByMe && !subjectIsMe) action = 'approve';
    const who = r.approver.user_id ? you(r.approver.user_id) : 'nobody eligible';
    label = r.decision.decision === 'revoke' ? 'To remove' : 'Kept';
    meta = `reviewed by ${you(r.decision.decided_by_user_id)} · approval: ${who}${r.approver.reciprocal ? ' · reciprocal pair, routed to a third holder' : ''}`;
  } else {
    label = 'Approved';
    meta = `${r.decision!.decision === 'revoke' ? 'revoke' : 'keep'} · approved by ${you(r.approval.approver_user_id)}`;
  }
  const state = `${label} · ${meta}`;
  const exception = r.reviewer.status !== 'routed' || r.approver.reciprocal || r.decision?.decision === 'revoke' || subjectIsMe || (!!r.decision && !r.approver.user_id && !r.approval);
  return { row: r, action, state, label, meta, exception };
}

/** Mobile quick path vs exceptions: rows needing judgement first, routine rows after. */
export function groupRows(rows: readonly AccessReviewRowStatus[], me: string, names: Names): { exceptions: RowView[]; routine: RowView[] } {
  const views = rows.map((r) => rowView(r, me, names));
  return { exceptions: views.filter((v) => v.exception), routine: views.filter((v) => !v.exception) };
}
